import React, { useEffect } from 'react'
import { useStateValue } from '@State'
import { saveAnswer } from '../../api'
import { FeedbackDisplay } from './utils/FeedbackDisplay'
import { SaveIndicator } from './utils/SaveIndicator'
import { TabSplitLayout } from './utils/TabSplitLayout'
import {
  FEEDBACK_COLORS, FEEDBACK_LABELS, getOptionFeedbackStatus,
  OPTION_FEEDBACK_STYLE, FEEDBACK_BADGE,
  FEEDBACK_KEY_STYLE, FEEDBACK_KEY_ITEM_STYLE, FEEDBACK_KEY_SWATCH,
} from './utils/feedbackStyles'

const TRUE_FALSE_OPTIONS = ['True', 'False']

const OPTION_BASE = {
  display: 'flex', alignItems: 'center',
  border: '2px solid #dee2e6', borderRadius: '10px',
  padding: '0.6rem 0.85rem', marginBottom: '0.5rem',
  backgroundColor: '#f8f9fa',
}

const LABEL_BASE = { display: 'flex', alignItems: 'center', margin: 0, flex: 1, cursor: 'pointer', gap: '0.5rem' }
const LABEL_DISABLED = { ...LABEL_BASE, cursor: 'default' }
const INPUT_STYLE = { width: '18px', height: '18px', flexShrink: 0, cursor: 'pointer' }

export const TrueFalseQuestion = ({ question, renderTabs = true }) => {
  const [state, dispatch] = useStateValue()
  const { coursenum, activitySubmissionId, savingGuids, readonly } = state
  const isSaving = savingGuids.has(question.guid)

  useEffect(() => {
    const selected = question.answer ? 1 : 0
    const { minSelections, maxSelections } = question.data
    dispatch({
      type: 'SET_FULLY_ANSWERED', guid: question.guid,
      value: selected >= minSelections && selected <= maxSelections
    })
  }, [question.answer])

  const handleChange = async newAnswer => {
    if (readonly || isSaving) return

    dispatch({ type: 'SET_ANSWER', guid: question.guid, answer: newAnswer })
    dispatch({ type: 'SAVE_START', guid: question.guid })

    try {
      const response = await saveAnswer(coursenum, activitySubmissionId, question.guid, newAnswer)
      dispatch({ type: 'SAVE_SUCCESS', guid: question.guid, payload: response.data })
    } catch (e) {
      dispatch({ type: 'SAVE_ERROR', guid: question.guid })
    }
  }

  const { showFeedback } = state
  const { showAnswer } = state.feedbackSettings
  const showOptionFeedback = showFeedback && showAnswer && !!question.correctAnswer

  return (
    <>
      <FeedbackDisplay question={question} />
      <div className='position-relative'>
        <SaveIndicator questionGuid={question.guid} />
      </div>
      <TabSplitLayout question={question} renderTabs={renderTabs}>
      <div className="d-flex flex-column align-items-start" style={{ width: '100%' }}>
        <div
          role="heading"
          aria-level={3}
          className="mb-2 text-left"
          dangerouslySetInnerHTML={{ __html: question.text }}
        />

        {showOptionFeedback && (
          <div style={FEEDBACK_KEY_STYLE}>
            <span>Key:</span>
            <span style={FEEDBACK_KEY_ITEM_STYLE}>
              <span style={{ ...FEEDBACK_KEY_SWATCH, borderColor: FEEDBACK_COLORS.correct.border, backgroundColor: FEEDBACK_COLORS.correct.bg }} />
              Your correct answer
            </span>
            <span style={FEEDBACK_KEY_ITEM_STYLE}>
              <span style={{ ...FEEDBACK_KEY_SWATCH, borderColor: FEEDBACK_COLORS.incorrect.border, backgroundColor: FEEDBACK_COLORS.incorrect.bg }} />
              Your wrong answer
            </span>
            <span style={FEEDBACK_KEY_ITEM_STYLE}>
              <span style={{ ...FEEDBACK_KEY_SWATCH, borderColor: FEEDBACK_COLORS.missed.border, backgroundColor: FEEDBACK_COLORS.missed.bg }} />
              Correct (missed)
            </span>
          </div>
        )}

        <div id={question.guid} style={{ width: '100%' }}>
          {TRUE_FALSE_OPTIONS.map(value => {
            const isSelected = question.answer === value
            const isCorrectOpt = question.correctAnswer === value

            let optionStyle = OPTION_BASE
            let badge = null
            if (showOptionFeedback) {
              const status = getOptionFeedbackStatus(isSelected, isCorrectOpt)
              optionStyle = { ...OPTION_BASE, ...OPTION_FEEDBACK_STYLE[status] }
              if (status !== 'neutral') {
                badge = <span style={FEEDBACK_BADGE[status]}>{FEEDBACK_LABELS[status]}</span>
              }
            }

            return (
              <div key={value} style={optionStyle}>
                <label style={readonly ? LABEL_DISABLED : LABEL_BASE}>
                  <input
                    type="radio"
                    name={`tf-${question.guid}`}
                    value={value}
                    checked={isSelected}
                    disabled={readonly}
                    onChange={() => handleChange(value)}
                    style={INPUT_STYLE}
                  />
                  {value}
                </label>
                {badge}
              </div>
            )
          })}
        </div>
      </div>
      </TabSplitLayout>
    </>
  )
}
